import { makeObservable, observable, action } from 'mobx'
import { get } from 'lodash'
import dayjs from 'dayjs'
/**
 * HistoryItem represents a single entry in a history category.
 *
 * @class HistoryItem
 * @param {Object} params - History item parameters.
 * @param {string} params.forSubtaskId - Identifier for the subtask this entry relates to.
 * @param {number|string} params.forRecurenceDate - Recurrence date associated with this entry.
 * @param {string} params.value - The value or status recorded in this history entry.
 * @param {string} params.createdBy - User ID who created this history entry.
 * @property {number} createdTs - Unix timestamp when the entry was created.
 */
class HistoryItem {
	forRecurenceDate
	forSubtaskId
	value
	createdBy
	createdTs = dayjs().unix()

	constructor({ forSubtaskId, forRecurenceDate, value, createdBy }) {
		this.forSubtaskId = forSubtaskId
		this.forRecurenceDate = forRecurenceDate
		this.value = value
		this.createdBy = createdBy

		makeObservable(this, {
			forSubtaskId: observable,
			forRecurenceDate: observable,
			value: observable,
			createdBy: observable,
			createdTs: observable
		})
	}
}

/**
 * History manages collections of HistoryItem entries grouped by category and ID.
 *
 * @class History
 * @param {Object} [history={}] - Initial history data structured as { [category]: { [id]: HistoryItem[] } }.
 */
export class History {
	categories = {}
	constructor(history = {}) {
		this.categories = history

		makeObservable(this, {
			categories: observable,
			addHistory: action,
			removeHistory: action,
			clearHistory: action,
			resetHistory: action
		})
	}

	/**
	 * Ensures the specified category exists.
	 *
	 * @param {string} category - The history category to vet.
	 */
	vetCategory(category) {
		if (!this.categories[category]) {
			this.categories[category] = {}
		}
	}

	/**
	 * Adds a new HistoryItem to the specified category and ID.
	 *
	 * @param {string} category - Category name under which to store the entry.
	 * @param {string|number} id - Identifier grouping entries (e.g., subtask ID).
	 * @param {Object} item - Parameters for the new HistoryItem.
	 */
	addHistory(category, id, item) {
		this.vetCategory(category)
		const _item = new HistoryItem(item)

		if (!this.categories[category][id]) {
			this.categories[category][id] = []
		}
		this.categories[category][id].push(_item)
	}

	/**
	 * Removes all history entries for a given category and ID.
	 *
	 * @param {string} category - Category name.
	 * @param {string|number} id - Identifier whose entries should be removed.
	 */
	removeHistory(category, id) {
		if (this.categories[category]) {
			delete this.categories[category][id]
		}
	}

	/**
	 * Clears all entries within a given category.
	 *
	 * @param {string} category - Category name to clear.
	 */
	clearHistory(category) {
		if (this.categories[category]) {
			this.categories[category] = {}
		}
	}

	/**
	 * Resets the entire history, removing all categories and entries.
	 */
	resetHistory() {
		this.categories = {}
	}

	/**
	 * Retrieves the raw history object for a category.
	 *
	 * @param {string} category - Category name.
	 * @returns {Object} The history data under the category.
	 */
	getHistory(category) {
		return get(this.categories, [category], {})
	}

	/**
	 * Retrieves history entries for a specific ID, optionally only the most recent.
	 *
	 * @param {string} category - Category name.
	 * @param {string|number} id - Identifier to filter entries by.
	 * @param {boolean} [lastHistoryOnly=false] - If true, return only the latest entry.
	 * @returns {HistoryItem[]|HistoryItem} Sorted entries or single most recent.
	 */
	getHistoryItem(category, id, lastHistoryOnly = false) {
		if (!this.categories[category] || !this.categories[category][id])
			return []
		const history = this.categories[category][id]
			.slice()
			.sort((a, b) => b.createdTs - a.createdTs)

		return lastHistoryOnly ? history[0] : history
	}

	/**
	 * Retrieves full history across all IDs for a category, optionally filtered by a subtask suffix.
	 *
	 * @param {string} category - Category name.
	 * @param {string|number} id - Identifier (or recurrence key) to match against.
	 * @param {boolean} [getAllValues=false] - If true, returns entries from all IDs.
	 * @returns {HistoryItem[]} Array of matching HistoryItem entries, sorted newest first.
	 */
	getFullHistoryItem(category, id, getAllValues = false) {
		const _id = id.toString().indexOf('_') > -1 ? id.split('_').pop() : id
		const history = this.getHistory(category)
		const fullHistory = []
		if (getAllValues) {
			Object.keys(history).forEach((key) => {
				fullHistory.push(...history[key])
			})
		} else {
			Object.keys(history).forEach((key) => {
				if (key.toString().indexOf(_id.toString()) > -1) {
					fullHistory.push(...history[key])
				}
			})
		}
		// sort by createdTs
		fullHistory.sort((a, b) => b.createdTs - a.createdTs)
		return fullHistory
	}
}
